import { Injectable } from '@angular/core';
import { ScanNutritionService } from './scan-nutrition.service';
import { EMPTY, Observable, of, OperatorFunction, throwError } from 'rxjs';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { PipelineStageConfig, StatusObject } from '../../interfaces/biozoom-api/pipeline';
import { AoxMeasurementResult } from '../../interfaces/biozoom-api/aox-measurement-result';
import { ScannerApiService } from '../scanner/scanner-api.service';
import { BiozoomError } from '../../interfaces/scanner-api/biozoom-error';
import { State } from '../../interfaces/biozoom-api/app-configuration';
import { catchError, delay, filter, first, retryWhen, scan, switchMap, tap, timeout } from 'rxjs/operators';
import { FileManagerService } from '../fileManager/file-manager.service';
import { environment } from '../../../environments/environment';
import { getLogs } from '../../methods/send-logs';
import {checkScannerBattery, getKeyVal} from 'src/app/methods/helpers';
import {processMeasurementAox, processMeasurementsAox, validateMeasurementAllowance} from '../../methods/measurement';
import {logError, mergeErrors} from '../../methods/error';
import { getServerDisplayName } from '../../methods/server-display';
import { getUploadHttpStatus, logUploadNetworkSummary } from '../../methods/upload-network-log';

@Injectable({
  providedIn: 'root'
})
/**
 * the purpose of this service is to encapsulate the Observables
 * provided by 'ScanNutritionService', updating states depending on their result,
 * and returning a value only once fully completed.
 */
export class ScanNutritionSinglesService {

  constructor(
    private scanNutritionService: ScanNutritionService,
    private scannerApiService: ScannerApiService,
    private fileManagerService: FileManagerService,
  ) { }

  // deprecated, use 'if' method instead
  selectAOXPrecise = (pipelineConfig: PipelineStageConfig): OperatorFunction<StatusObject, boolean> => {
    return switchMap((_stati: StatusObject) => {
      if (pipelineConfig.reduceQueries) {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onReduceQueries, 'onReduceQueries', pipelineConfig.stage);
      } else {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onNormalProcess, 'onNormalProcess', pipelineConfig.stage);
      }
      return of(true);
    });
  }

  /**
   * execute a single aox measurement.
   * the next stage is called as soon as the measurement has been received.
   * @param pipelineConfig bundle object containing all meta-parameters and getter/setter functions for the measurement
   * @param measurementType type of the measurement
   */
  startMeasurement = (
    pipelineConfig: PipelineStageConfig,
    measurementType: string = 'full',
  ) => {
    return switchMap((_stati: StatusObject) => {
      let currentIDX = pipelineConfig.getStep().idx;
      if (!validateMeasurementAllowance(currentIDX > 0 ? 'aoxPrecise' : 'aox', pipelineConfig.state.persistentValues)) {
        const onInsufficientCredits = pipelineConfig?.stageConfig?.eventTargets?.onInsufficientCredits;
        if (onInsufficientCredits) {
          pipelineConfig.executeStage(
            onInsufficientCredits,
            'onInsufficientCredits',
            pipelineConfig.stage
          );
        } else {
          pipelineConfig.showToast?.(
            'Your Scanner is licenced to be using coins, but your current config does not support this. Please contact customer support.',
            10000,
            'top'
          );
          pipelineConfig.executeStage(
            environment.appDesignSettings.homeStage ?? '',
            'onInsufficientCreditsMissingTarget',
            pipelineConfig.stage
          );
        }
        return EMPTY;
      }
      const getKey = pipelineConfig.stageConfig.getKey ?? '';
      const getGroup = pipelineConfig.stageConfig.getGroup ?? '';
      const newIDX = Number(getKeyVal(getKey, pipelineConfig.state?.[getGroup] ?? {}) ?? '-1');
      if (newIDX > -1) {
        currentIDX = newIDX;
      }
      pipelineConfig.setStep({
        progress: 0,
        idx: currentIDX,
      });
      return this.scanNutritionService.startMeasurement(
        pipelineConfig.state.persistentValues,
        measurementType
      ).pipe(
        // Will time out after 13s.
        // This will no longer happen once the base-observable finishes.
        timeout(pipelineConfig.state.persistentValues.SCANNER_VERSION === 2 ? 21000 : 13000),
        switchMap((event) => {
          switch (event.type) {
            case HttpEventType.Response: {
              if (event.body === '') {
                pipelineConfig.executeStage(
                  pipelineConfig?.stageConfig?.eventTargets?.onBadMeasurement,
                  'onBadMeasurement',
                  pipelineConfig.stage
                );
                return EMPTY;
              }
              let measurementTypeClean = measurementType;
              if (measurementTypeClean === 'front_half' && (event?.body?.length ?? 254012) !== 127028) {
                measurementTypeClean = 'full';
              }
              const newStep = {
                measurement: { data: event.body, type: measurementTypeClean },
                idx: currentIDX
              };
              if (pipelineConfig.state.persistentValues.CONNECTION_TYPE === 'USB') {
                // @ts-ignore
                newStep.progress = 1;
              }
              pipelineConfig.setStep(newStep);
              return of(true);
            }
            case HttpEventType.DownloadProgress: {
              pipelineConfig.setStep({ progress: (event.loaded / event.total), idx: currentIDX });
              return of(false);
            }
            default: {
              return of(false);
            }
          }
        }),
        filter((el) => !!el),
        delay(100),
        first(),
        tap(() => {
          pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
        }),
        catchError(e => {
          if (typeof e === 'object') {
            logError(e);
            if (e.name !== undefined && e.name === 'TimeoutError') {
              pipelineConfig.executeStage(
                pipelineConfig?.stageConfig?.eventTargets?.onBadMeasurement,
                'onBadMeasurement',
                pipelineConfig.stage
              );
            }
          }
          return of(false);
        })
      );
    });
  }

  /**
   * Starts the transparent AOX upload flow by resolving the VWS endpoint.
   * The routing response is intentionally kept in temp state: configs may
   * insert arbitrary pages between this step and the raw-data upload.
   */
  preRequestAox = (pipelineConfig: PipelineStageConfig): OperatorFunction<StatusObject, boolean> => {
    return switchMap((stati: StatusObject) => {
      const currentIDX = pipelineConfig.getStep().idx;
      if (stati._measurements.length === stati._aoxData.length) {
        pipelineConfig.executeStage(
          pipelineConfig?.stageConfig?.eventTargets?.onInsufficientMeasurements,
          'onInsufficientMeasurements', pipelineConfig.stage
        );
        return of(true);
      }

      const measurement = pipelineConfig.getStep().measurements[currentIDX];
      if (!measurement) {
        return this.failTransparentUpload(pipelineConfig, 'preRequest', new Error('No AOX measurement available.'));
      }
      let additionalData = {
        status_before: stati._statusBefore ?? {},
        status_after: stati._statusAfter ?? {},
        app_version: environment.appVersion,
        config: environment.appDesignName,
        localTimestamp: Date.now(),
        localLog: getLogs(),
        aox_subtype: measurement.type,
      };
      if (pipelineConfig.state.additionalData) {
        additionalData = { ...additionalData, ...pipelineConfig.state.additionalData };
      }
      const routingServers = this.scanNutritionService.getAoxUploadServers();
      const routingCandidates = this.createAoxRoutingCandidates(
        routingServers,
        pipelineConfig.state?.persistentValues?.vwsPreRequestTimeoutMs,
        pipelineConfig.state?.persistentValues?.vwsPreRequestMaxTimeoutAttempts,
      );
      const context = {
        idx: currentIDX,
        serial: pipelineConfig.scanner.serial,
        data: measurement.data,
        additionalData,
        route: undefined,
        result: undefined,
        routingIndex: 0,
        routingServers,
        routingCandidates,
        maxPreRequestAttempts: routingCandidates.length ? routingCandidates[routingCandidates.length - 1].round + 1 : 0,
        attemptedRoutingServers: [],
        attemptedRecommendedServers: [],
      };
      pipelineConfig.setStep({ progress: 0, idx: currentIDX });
      this.setTransparentUploadState(pipelineConfig, context, {
        phase: 'preRequest',
        phases: this.createTransparentUploadPhases('preRequest'),
        attempt: 1,
        maxRetries: context.maxPreRequestAttempts,
        routingServer: undefined,
      });
      return this.requestNextAoxRoute(
        pipelineConfig, context, pipelineConfig.getState(State.DEMO_MODE), 0,
        new Error('No VWS routing server available.')
      ).pipe(
        switchMap(({route, index}: any) => {
          context.route = route;
          context.routingIndex = index;
          context.attemptedRecommendedServers = [route.recommended_server];
          this.setTransparentUploadPhase(pipelineConfig, 'preRequest', 'completed');
          this.setTransparentUploadState(pipelineConfig, context, {
            phase: 'serverSelected',
            phases: this.withTransparentUploadPhase(pipelineConfig, 'serverSelected', 'completed'),
            recommendedServer: route.recommended_server,
            waitingTimeMs: route.waiting_time_ms,
            attempt: 1,
            retryPhase: undefined,
            error: undefined,
          });
          this.logNetworkSummary(pipelineConfig, 'preRequest', 'success', context, route.transportHttpStatus);
          pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
          return of(true);
        }),
        catchError(error => this.failTransparentUpload(pipelineConfig, 'preRequest', error))
      );
    });
  }

  /** Upload raw AOX data using the VWS endpoint selected by preRequestAOX. */
  uploadRawDataAox = (pipelineConfig: PipelineStageConfig): OperatorFunction<StatusObject, boolean> => {
    return switchMap((_stati: StatusObject) => {
      const context = pipelineConfig.state?.temp?.aoxUploadContext;
      if (!context?.route || !context?.data) {
        return this.failTransparentUpload(pipelineConfig, 'rawData', new Error('Missing AOX upload context.'));
      }
      const demoMode = pipelineConfig.getState(State.DEMO_MODE);
      const language = pipelineConfig.getState(State.SELECTED_LANGUAGE);
      const currentIDX = context.idx;
      const vwsUpload = this.uploadAoxToRecommendedServer(pipelineConfig, context, demoMode, language);

      return vwsUpload.pipe(
        catchError(error => error === 'license'
          ? throwError(error)
          : this.rerouteAoxUpload(
            pipelineConfig, context, demoMode, language, (context.routingIndex ?? 0) + 1, error
          )),
        switchMap((event: HttpEvent<AoxMeasurementResult>) => {
          switch (event.type) {
            case HttpEventType.Sent:
              this.setTransparentUploadStatus(pipelineConfig, { phase: 'rawDataSending' });
              return of(false);
            case HttpEventType.UploadProgress: {
              const progress = event.total ? event.loaded / event.total : 0;
              pipelineConfig.setStep({ progress: progress * 0.9, idx: currentIDX });
              if (event.total && event.loaded === event.total) {
                this.setTransparentUploadPhase(pipelineConfig, 'rawDataSending', 'completed');
                this.setTransparentUploadPhase(pipelineConfig, 'rawDataSent', 'completed');
                this.setTransparentUploadPhase(pipelineConfig, 'resultsRetrieving', 'active', { phase: 'resultsRetrieving' });
              }
              return of(false);
            }
            case HttpEventType.DownloadProgress: {
              const progress = event.total ? event.loaded / event.total : 0;
              pipelineConfig.setStep({ progress: (progress * 0.1) + 0.9, idx: currentIDX });
              this.setTransparentUploadPhase(pipelineConfig, 'resultsRetrieving', 'active', { phase: 'resultsRetrieving' });
              return of(false);
            }
            case HttpEventType.Response:
              context.result = event.body;
              this.setTransparentUploadPhase(pipelineConfig, 'rawDataSending', 'completed');
              this.setTransparentUploadPhase(pipelineConfig, 'rawDataSent', 'completed');
              this.setTransparentUploadPhase(pipelineConfig, 'resultsRetrieving', 'completed');
              this.setTransparentUploadState(pipelineConfig, context, {
                phase: 'resultRetrieved',
                phases: this.withTransparentUploadPhase(pipelineConfig, 'resultRetrieved', 'completed'),
              });
              this.logNetworkSummary(pipelineConfig, 'rawDataUpload', 'success', context, event.status);
              pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
              return of(true);
            default:
              return of(false);
          }
        }),
        filter(result => result),
        catchError(error => this.failTransparentUpload(pipelineConfig, 'rawData', error))
      );
    });
  }

  /** Finalize a raw result stored by uploadRawDataAOX using the legacy result rules. */
  processUploadResultAox = (pipelineConfig: PipelineStageConfig): OperatorFunction<StatusObject, boolean> => {
    return switchMap((stati: StatusObject) => {
      const context = pipelineConfig.state?.temp?.aoxUploadContext;
      if (!context?.result) {
        return this.failTransparentUpload(pipelineConfig, 'resultProcessing', new Error('Missing AOX upload result.'));
      }
      this.setTransparentUploadPhase(pipelineConfig, 'resultProcessing', 'active', { phase: 'resultProcessing' });
      const completed = this.applyAoxResult(pipelineConfig, stati, context.idx, context.result);
      this.setTransparentUploadPhase(
        pipelineConfig,
        'resultProcessing',
        completed ? 'completed' : 'failed',
        { phase: completed ? 'completed' : 'failed' }
      );
      if (completed) {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
      }
      return of(true);
    });
  }

  /** Upload against the VWS currently selected by the most recent pre-request. */
  private uploadAoxToRecommendedServer = (
    pipelineConfig: PipelineStageConfig,
    context: any,
    demoMode: boolean,
    language: string,
  ): Observable<HttpEvent<AoxMeasurementResult>> => {
    this.setTransparentUploadStatus(pipelineConfig, {
      phase: 'rawDataSending',
      attempt: 1,
      maxRetries: this.getAoxUploadMaxRetries(pipelineConfig),
      retryPhase: undefined,
      error: undefined,
      phases: this.withTransparentUploadPhase(pipelineConfig, 'rawDataSending', 'active'),
    });
    return this.scanNutritionService.uploadAoxToSelectedServer(
      demoMode, context.serial, context.data, context.route.waiting_time_ms,
      context.route.recommended_server, language, context.additionalData
    ).pipe(this.retryTransparentRequest(pipelineConfig, 'rawData'));
  }

  /**
   * Resolve another VWS route after the current recommended server exhausted
   * its upload retries. Routing candidates continue with the next VWS and,
   * after every VWS was attempted, with the next pre-request timeout round.
   */
  private rerouteAoxUpload = (
    pipelineConfig: PipelineStageConfig,
    context: any,
    demoMode: boolean,
    language: string,
    routingIndex: number,
    lastError: any,
  ): Observable<HttpEvent<AoxMeasurementResult>> => {
    return this.requestNextAoxRoute(pipelineConfig, context, demoMode, routingIndex, lastError).pipe(
      switchMap(({route, index}: any) => {
        context.route = route;
        context.routingIndex = index;
        context.attemptedRecommendedServers = [
          ...(context.attemptedRecommendedServers ?? []),
          route.recommended_server,
        ];
        this.setTransparentUploadPhase(pipelineConfig, 'preRequest', 'completed');
        this.setTransparentUploadState(pipelineConfig, context, {
          phase: 'serverSelected',
          phases: this.withTransparentUploadPhase(pipelineConfig, 'serverSelected', 'completed'),
          recommendedServer: route.recommended_server,
          waitingTimeMs: route.waiting_time_ms,
          attempt: 1,
          retryPhase: undefined,
          error: undefined,
        });
        return this.uploadAoxToRecommendedServer(pipelineConfig, context, demoMode, language).pipe(
          catchError(error => error === 'license'
            ? throwError(error)
            : this.rerouteAoxUpload(
              pipelineConfig, context, demoMode, language, index + 1, error
            ))
        );
      })
    );
  }

  /**
   * Request the next VWS route. Every configured VWS is queried once per
   * timeout round: 500 ms, 2,000 ms, then the configured maximum timeout.
   */
  private requestNextAoxRoute = (
    pipelineConfig: PipelineStageConfig,
    context: any,
    demoMode: boolean,
    routingIndex: number,
    lastError: any,
  ): Observable<any> => {
    const routingCandidates = context.routingCandidates ?? this.createAoxRoutingCandidates(
      context.routingServers ?? this.scanNutritionService.getAoxUploadServers(),
      pipelineConfig.state?.persistentValues?.vwsPreRequestTimeoutMs,
      pipelineConfig.state?.persistentValues?.vwsPreRequestMaxTimeoutAttempts,
    );
    if (routingIndex >= routingCandidates.length) {
      return throwError(lastError);
    }
    const routingCandidate = routingCandidates[routingIndex];
    const routingServer = routingCandidate.server;
    const isRetry = routingIndex > 0;
    context.attemptedRoutingServers = [...(context.attemptedRoutingServers ?? []), routingServer];

    this.setTransparentUploadStatus(pipelineConfig, {
      phase: 'preRequest',
      attempt: routingCandidate.round + 1,
      maxRetries: context.maxPreRequestAttempts ?? routingCandidate.round + 1,
      routingServer,
      retryPhase: isRetry ? 'preRequest' : undefined,
      error: isRetry ? this.uploadError(lastError) : undefined,
      phases: this.withTransparentUploadPhase(pipelineConfig, 'preRequest', 'active'),
    });
    return this.scanNutritionService.requestAoxUploadServerOnServer(
      demoMode, context.serial, routingServer, routingCandidate.timeoutMs
    ).pipe(
      switchMap((route: any) => {
        if ((context.attemptedRecommendedServers ?? []).includes(route.recommended_server)) {
          return throwError(new Error('VWS returned an already attempted recommended server.'));
        }
        return of({route, index: routingIndex});
      }),
      catchError(error => error === 'license'
        ? throwError(error)
        : this.requestNextAoxRoute(
          pipelineConfig, context, demoMode, routingIndex + 1, error
        ))
    );
  }

  private createAoxRoutingCandidates = (
    routingServers: string[], configuredTimeoutMs: any, configuredMaxTimeoutAttempts: any
  ): any[] => {
    const configuredTimeout = Number(configuredTimeoutMs);
    const maximumTimeoutMs = Number.isFinite(configuredTimeout) && configuredTimeout > 0
      ? configuredTimeout
      : 10000;
    const maximumTimeoutAttempts = this.getAoxPreRequestMaxTimeoutAttempts(configuredMaxTimeoutAttempts);
    const timeoutRounds = [500, 2000]
      .filter(timeoutMs => timeoutMs < maximumTimeoutMs)
      .concat(Array(maximumTimeoutAttempts).fill(maximumTimeoutMs));

    return timeoutRounds.reduce((candidates: any[], timeoutMs, round) => {
      routingServers.forEach(server => candidates.push({ server, timeoutMs, round }));
      return candidates;
    }, []);
  }

  private retryTransparentRequest = <T>(
    pipelineConfig: PipelineStageConfig,
    phase: string,
  ): OperatorFunction<T, T> => {
    return retryWhen(errors => errors.pipe(
      scan((attempt: number, error: any) => {
        const maxRetries = this.getAoxUploadMaxRetries(pipelineConfig);
        if (attempt >= maxRetries || error === 'license') {
          throw error;
        }
        const retryWorkflowPhase = this.workflowPhaseForUploadPhase(phase);
        this.setTransparentUploadStatus(pipelineConfig, {
          phase: 'retrying', retryPhase: phase, attempt: attempt + 2, maxRetries,
          error: this.uploadError(error),
          phases: this.withTransparentUploadPhase(pipelineConfig, retryWorkflowPhase, 'active'),
        });
        return attempt + 1;
      }, 0),
      delay(1000),
    ));
  }

  private getAoxPreRequestMaxTimeoutAttempts = (value: any): number => {
    const attempts = Number(value);
    return Number.isInteger(attempts) && attempts > 0 ? attempts : 1;
  }

  private getAoxUploadMaxRetries = (pipelineConfig: PipelineStageConfig): number => {
    const retries = Number(pipelineConfig.state?.persistentValues?.vwsUploadMaxRetries);
    return Number.isInteger(retries) && retries > 0 ? retries : 5;
  }

  private failTransparentUpload = (pipelineConfig: PipelineStageConfig, phase: string, error: any): Observable<boolean> => {
    this.setTransparentUploadStatus(pipelineConfig, {
      phase: 'failed',
      failedPhase: phase,
      error: this.uploadError(error),
      phases: this.withTransparentUploadPhase(pipelineConfig, this.workflowPhaseForUploadPhase(phase), 'failed'),
    });
    if (phase === 'preRequest' || phase === 'rawData') {
      this.logNetworkSummary(
        pipelineConfig,
        phase === 'preRequest' ? 'preRequest' : 'rawDataUpload',
        'failed',
        pipelineConfig.state?.temp?.aoxUploadContext,
        getUploadHttpStatus(error),
        error,
      );
    }
    if (error === 'license') {
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onNoLicense, 'onNoLicense', pipelineConfig.stage);
    } else {
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onError, 'onError', pipelineConfig.stage);
    }
    return of(true);
  }

  private logNetworkSummary = (
    pipelineConfig: PipelineStageConfig,
    operation: 'preRequest' | 'rawDataUpload',
    outcome: 'success' | 'failed',
    context: any,
    httpStatus: number | null | undefined,
    error?: any,
  ) => {
    const status = pipelineConfig.state?.temp?.aoxUploadStatus ?? {};
    const nextStage = outcome === 'success'
      ? pipelineConfig.stageConfig?.eventTargets?.onSuccess
      : error === 'license'
        ? pipelineConfig.stageConfig?.eventTargets?.onNoLicense
        : pipelineConfig.stageConfig?.eventTargets?.onError;
    logUploadNetworkSummary(pipelineConfig, nextStage, {
      measurement: 'aox',
      operation,
      outcome,
      attempt: status.attempt,
      maxRetries: status.maxRetries,
      httpStatus: httpStatus ?? null,
      routingServer: status.routingServer,
      recommendedServer: status.recommendedServer,
      attemptedRoutingServers: context?.attemptedRoutingServers ?? [],
      attemptedRecommendedServers: context?.attemptedRecommendedServers ?? [],
      ...(error ? {error: {name: error?.name, message: error?.message ?? String(error)}} : {}),
    });
  }

  private setTransparentUploadState = (pipelineConfig: PipelineStageConfig, context: any, status: any) => {
    pipelineConfig.setState?.('aoxUploadContext', context, 'temp');
    this.setTransparentUploadStatus(pipelineConfig, status);
  }

  private setTransparentUploadStatus = (pipelineConfig: PipelineStageConfig, update: any) => {
    const current = pipelineConfig.state?.temp?.aoxUploadStatus ?? {};
    pipelineConfig.setState?.('aoxUploadStatus', {
      ...current,
      ...this.withServerDisplayNames(update),
    }, 'temp');
  }

  private withServerDisplayNames = (update: any) => {
    const status = {...update};
    for (const key of ['routingServer', 'recommendedServer']) {
      if (Object.prototype.hasOwnProperty.call(status, key)) {
        status[key] = getServerDisplayName(status[key]);
      }
    }
    return status;
  }

  private createTransparentUploadPhases = (activePhase: string) => {
    const phases = [
      'preRequest',
      'serverSelected',
      'rawDataSending',
      'rawDataSent',
      'resultsRetrieving',
      'resultRetrieved',
      'resultProcessing',
    ];
    return phases.reduce((result, phase) => ({ ...result, [phase]: phase === activePhase ? 'active' : 'pending' }), {});
  }

  private withTransparentUploadPhase = (pipelineConfig: PipelineStageConfig, phase: string, state: string) => {
    const phases = pipelineConfig.state?.temp?.aoxUploadStatus?.phases ?? this.createTransparentUploadPhases('');
    return { ...phases, [phase]: state };
  }

  private setTransparentUploadPhase = (
    pipelineConfig: PipelineStageConfig, phase: string, state: string, update: any = {}
  ) => {
    this.setTransparentUploadStatus(pipelineConfig, {
      ...update,
      phases: this.withTransparentUploadPhase(pipelineConfig, phase, state),
    });
  }

  private workflowPhaseForUploadPhase = (phase: string): string => {
    switch (phase) {
      case 'preRequest':
        return 'preRequest';
      case 'resultProcessing':
        return 'resultProcessing';
      default:
        return 'rawDataSending';
    }
  }

  private uploadError = (error: any): string => {
    if (typeof error === 'string') {
      return error;
    }
    return error?.name ?? error?.message ?? 'unknown_error';
  }

  private applyAoxResult = (
    pipelineConfig: PipelineStageConfig, stati: StatusObject, currentIDX: number, res: AoxMeasurementResult
  ): boolean => {
    const runs = pipelineConfig.getState(State.AOX_MEASUREMENT_RUNS) + 1;
    pipelineConfig.setState(State.AOX_MEASUREMENT_RUNS, runs);
    res.aox_decimal = res.aox;
    const displayErrors = processMeasurementAox(res);
    const previousResults = pipelineConfig.getState(State.RESULT_AVG) ?? {};
    pipelineConfig.setState(State.RESULT_AVG, mergeErrors(previousResults, res ?? {}), 'persistentValues');
    pipelineConfig.setState('displayErrors', displayErrors, 'temp');
    const aox = [...pipelineConfig.getStep().aox] ?? [];
    aox.push(res);
    processMeasurementsAox(aox);
    pipelineConfig.setStep({ aoxData: aox });
    this.fileManagerService.addToCsv(res, pipelineConfig, undefined, pipelineConfig.state.additionalDataStore ?? {});
    pipelineConfig.setStep({ idx: currentIDX + 1 });
    pipelineConfig.setState(State.AOX_MEASUREMENT_RESULTS, aox);
    pipelineConfig.setState('resultAox', res, 'temp');

    if (res.aox_q >= 50) {
      const targets: {[key: number]: [string, string, string]} = {
        100: ['onError100', 'Error100', BiozoomError.ERROR_100],
        90: ['onError90', 'BatteryError', BiozoomError.BATTERY],
        80: ['onError80', 'Error80', BiozoomError.ERROR_80],
        70: ['onError70', 'Error70', BiozoomError.ERROR_70],
        60: ['onError60', 'Error60', BiozoomError.LIGHT_60],
        50: ['onError50', 'Error50', BiozoomError.LIGHT_50],
        45: ['onError45', 'Error45', BiozoomError.ERROR_45],
        44: ['onError44', 'Error44', BiozoomError.ERROR_44],
      };
      const target = targets[res.aox_q];
      if (target) {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.[target[0]] ?? target[2], target[1], pipelineConfig.stage);
      } else {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onError, 'Error', pipelineConfig.stage);
      }
      return false;
    }

    // Kept in sync with the legacy path while critical-error detection is disabled there.
    if (checkScannerBattery(stati._statusAfter, pipelineConfig.state.appSettings.batteryPrewarningVoltage)) {
      return true;
    }
    pipelineConfig.executeStage(
      pipelineConfig?.stageConfig?.eventTargets?.onErrorBattery ?? BiozoomError.BATTERY,
      'BatteryError', pipelineConfig.stage
    );
    return false;
  }


  /**
   * @deprecated Legacy single-stage AOX upload retained for existing configs.
   * New configs should use preRequestAox, uploadRawDataAox and processUploadResultAox.
   *
   * Upload a single aox measurement to be processed and analyzed.
   * the next state will be called once the server response has been received
   * @param pipelineConfig bundle object containing all meta-parameters and getter/setter functions for the measurement
   */
  uploadData = (
    pipelineConfig: PipelineStageConfig
  ) => {
    return switchMap((stati: StatusObject) => {
      const currentIDX = pipelineConfig.getStep().idx;
      // in case it has been decided that an upload should be done, but all current measurements have been processed,
      // that means not enough measurements exist. proceed to the onInsufficientMeasurements-stage.
      if (stati._measurements.length === stati._aoxData.length) {
        pipelineConfig.executeStage(
          pipelineConfig?.stageConfig?.eventTargets?.onInsufficientMeasurements,
          'onInsufficientMeasurements',
          pipelineConfig.stage
        );
        return of(true);
      }
      let additionalData = {
        status_before: stati._statusBefore ?? {},
        status_after: stati._statusAfter ?? {},
        app_version: environment.appVersion,
        config: environment.appDesignName,
        localTimestamp: new Date(Date.now()).toString(),
        localLog: getLogs(),
      };
      if (pipelineConfig.state.additionalData) {
        additionalData = { ...additionalData, ...pipelineConfig.state.additionalData };
      }

      pipelineConfig.setStep({ progress: 0, idx: currentIDX });
      const measurement = pipelineConfig.getStep().measurements;

      const idx = currentIDX;
      const m = measurement[idx];

      return this.scanNutritionService.calculateMeasurementResult(
        pipelineConfig.getState(State.DEMO_MODE),
        pipelineConfig.getState(State.SELECTED_LANGUAGE),
        pipelineConfig.scanner.serial,
        m['data'],
        { ...additionalData, localTimestamp: Date.now(), aox_subtype: m['type'] },
        pipelineConfig.state?.persistentValues?.vwsPreRequestTimeoutMs,
      ).pipe(
        switchMap((event: any) => {
          if (event === false) {
            pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onNoLicense, 'No license', pipelineConfig.stage);
            return of([true, false]);
          }
          if ((event as HttpEvent<any>).type === undefined) {
            return of([false, false]);
          } else {
            event = event as HttpEvent<AoxMeasurementResult>;
            switch (event.type) {
              case HttpEventType.Response: {
                const runs = pipelineConfig.getState(State.AOX_MEASUREMENT_RUNS) + 1;
                pipelineConfig.setState(State.AOX_MEASUREMENT_RUNS, runs);
                const res: AoxMeasurementResult = event.body;
                res.aox_decimal = res.aox;
                const displayErrors = processMeasurementAox(res);
                const previousResults = pipelineConfig.getState(State.RESULT_AVG) ?? {};
                const resultMerged = mergeErrors(previousResults, res ?? {});
                pipelineConfig.setState(State.RESULT_AVG, resultMerged, 'persistentValues');
                pipelineConfig.setState('displayErrors', displayErrors, 'temp');
                const aox = [...pipelineConfig.getStep().aox] ?? [];
                aox.push(res);
                processMeasurementsAox(aox);
                pipelineConfig.setStep({
                  aoxData: aox
                });
                this.fileManagerService.addToCsv(res, pipelineConfig, undefined, pipelineConfig.state.additionalDataStore ?? {});
                pipelineConfig.setStep({ idx: currentIDX + 1 });
                pipelineConfig.setState(State.AOX_MEASUREMENT_RESULTS, aox);
                pipelineConfig.setState('resultAox', res, 'temp');
                // TODO: Re-enable
                // const errorTypes = getErrorCountAox(res);
                const errorTypes = [];
                if (res.aox_q >= 50) {
                  switch (res.aox_q) {
                    case 100:
                      // prototype scanner
                      pipelineConfig.executeStage(
                        pipelineConfig?.stageConfig?.eventTargets?.onError100 ?? BiozoomError.ERROR_100,
                        'Error100',
                        pipelineConfig.stage
                      );
                      return of([true, false]);
                    case 90:
                      // Scanner battery voltage too low, result unreliable
                      pipelineConfig.executeStage(
                        pipelineConfig?.stageConfig?.eventTargets?.onError90 ?? BiozoomError.BATTERY,
                        'BatteryError',
                        pipelineConfig.stage
                      );
                      return of([true, false]);
                    case 80:
                      // LED-voltage of scanner too low
                      pipelineConfig.executeStage(
                        pipelineConfig?.stageConfig?.eventTargets?.onError80 ?? BiozoomError.ERROR_80,
                        'Error80',
                        pipelineConfig.stage
                      );
                      return of([true, false]);
                    case 70:
                      // Data transmission error between device and scanner
                      pipelineConfig.executeStage(
                        pipelineConfig?.stageConfig?.eventTargets?.onError70 ?? BiozoomError.ERROR_70,
                        'Error70',
                        pipelineConfig.stage
                      );
                      return of([true, false]);
                    case 60:
                      // 1 or more light paths are vastly too bright
                      pipelineConfig.executeStage(
                        pipelineConfig?.stageConfig?.eventTargets?.onError60 ?? BiozoomError.LIGHT_60,
                        'Error60',
                        pipelineConfig.stage
                      );
                      return of([true, false]);
                    case 50:
                      // 3 or more light paths are too bright
                      pipelineConfig.executeStage(
                        pipelineConfig?.stageConfig?.eventTargets?.onError50 ?? BiozoomError.LIGHT_50,
                        'Error50',
                        pipelineConfig.stage
                      );
                      return of([true, false]);
                    case 45:
                      pipelineConfig.executeStage(
                        pipelineConfig?.stageConfig?.eventTargets?.onError45 ?? BiozoomError.ERROR_45,
                        'Error45',
                        pipelineConfig.stage
                      );
                      return of([true, false]);
                    case 44:
                      pipelineConfig.executeStage(
                        pipelineConfig?.stageConfig?.eventTargets?.onError44 ?? BiozoomError.ERROR_44,
                        'Error44',
                        pipelineConfig.stage
                      );
                      return of([true, false]);
                    default:
                      pipelineConfig.executeStage(
                        pipelineConfig?.stageConfig?.eventTargets?.onError,
                        'Error',
                        pipelineConfig.stage
                      );
                      return of([true, false]);
                  }
                } else {
                  if (errorTypes.length > 0) {
                    const errors = {};
                    for (const errorType of errorTypes) {
                      errors[errorType] = true;
                    }
                    pipelineConfig.setState('errors', errors, 'temp');
                    let target: string;
                    let event: string;
                    if (errorTypes.length > 1) {
                      target = pipelineConfig?.stageConfig?.eventTargets?.onErrorCritMulti;
                      event = 'ErrorCritMulti';
                    } else {
                      switch (errorTypes[0]) {
                        case 'repetition': {
                          target = pipelineConfig?.stageConfig?.eventTargets?.onErrorCritRepetition;
                          event = 'ErrorCritRepetition';
                          break;
                        }
                        case 'motion': {
                          target = pipelineConfig?.stageConfig?.eventTargets?.onErrorCritMotion;
                          event = 'ErrorCritMotion';
                          break;
                        }
                        case 'brightness': {
                          target = pipelineConfig?.stageConfig?.eventTargets?.onErrorCritBrightness;
                          event = 'ErrorCritBrightness';
                          break;
                        }
                        case 'position': {
                          target = pipelineConfig?.stageConfig?.eventTargets?.onErrorCritPosition;
                          event = 'ErrorCritPosition';
                          break;
                        }
                        case 'brightness_individual': {
                          target = pipelineConfig?.stageConfig?.eventTargets?.onErrorCritBrightnessIndividual;
                          event = 'ErrorCritBrightnessIndividual';
                          break;
                        }
                      }
                    }
                    if (target) {
                      pipelineConfig.executeStage(
                        target,
                        event,
                        pipelineConfig.stage
                      );
                      return of([true, false]);
                    }
                  }

                  if (checkScannerBattery(
                    stati._statusAfter,
                    pipelineConfig.state.appSettings.batteryPrewarningVoltage)
                  ) {
                    return of([true, true]);
                  } else {
                    pipelineConfig.executeStage(
                      pipelineConfig?.stageConfig?.eventTargets?.onErrorBattery ?? BiozoomError.BATTERY,
                      'BatteryError',
                      pipelineConfig.stage
                    );
                    return of([true, false]);
                  }
                }
                break;
              }
              case HttpEventType.Sent: {
                break;
              }
              case HttpEventType.UploadProgress:
              case HttpEventType.DownloadProgress: {
                const tmp = event.loaded / event.total;
                pipelineConfig.setStep(
                  {
                    progress: event.type === HttpEventType.UploadProgress ? (tmp * 0.9) : (tmp * 0.1) + 0.9,
                    idx: currentIDX
                  }
                );
                break;
              }
              default: {
                return of([false, false]);

              }
            }
          }
          return of([false, false]);
        }),
        filter(el => !!el[0]),
        tap((el) => {
          if (el[1]) {
            pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
          }
        })
      );
    });
  }

}
